import React from "react";
import { LoaderFunctionArgs, useLoaderData, useParams } from "react-router-dom";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { Divider } from "@nextui-org/react";
import BookingDetailsItem from "@/components/bookings/details/BookingDetailsItem";
import { ApiService } from "@/api/services/ApiService";
import { getFormattedTimeAndDate } from "@/lib/helpers";
import { IWorkOrder, IWorkOrderNotes, IWorkOrderPosts } from "@/lib/types";

// ---------------------- API

const notesQuery = (workOrderId: string) => ({
  queryKey: ["work-order-notes", workOrderId] as const,
  queryFn: async () => ApiService.bookings.getWorkOrderNotes(workOrderId),
});

const postsQuery = (workOrderId: string) => ({
  queryKey: ["work-order-posts", workOrderId] as const,
  queryFn: async () => ApiService.bookings.getWorkOrderPosts(workOrderId),
});

export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const bookingId = params.id;
    if (!bookingId) throw new Error("Booking ID not found");

    try {
      // First get the work order to get its ID
      const workOrder = await queryClient.ensureQueryData({
        queryKey: ["work-order-info", bookingId] as const,
        queryFn: async () => ApiService.bookings.getWorkOrder(bookingId),
      });

      // Now use the workOrderId for posts and notes
      const [notes, posts] = await Promise.all([
        queryClient.ensureQueryData(notesQuery(workOrder.workOrderId!)),
        queryClient.ensureQueryData(postsQuery(workOrder.workOrderId!)),
      ]);

      return { workOrder, notes, posts };
    } catch (error) {
      throw error; // Let React Router handle the error
    }
  };

const convertBrToLineBreaks = (text: string) => {
  if (!text) return "";

  const parts = text.split(/<br\s*\/?>/i);

  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

// ---------------------- Component

const BookingPostsNotes = () => {
  // --------- Variables / Hooks

  const { id = "" } = useParams();

  const initialData = useLoaderData() as {
    workOrder: IWorkOrder;
    notes: IWorkOrderNotes[];
    posts: IWorkOrderPosts[];
  };

  const { data: workOrder } = useQuery({
    queryKey: ["work-order-info", id],
    queryFn: async () => ApiService.bookings.getWorkOrder(id),
    initialData: initialData.workOrder,
  });

  const { data: notes = [] } = useQuery({
    ...notesQuery(workOrder.workOrderId!),
    initialData: initialData.notes,
  });

  const { data: posts = [] } = useQuery({
    ...postsQuery(workOrder.workOrderId!),
    initialData: initialData.posts,
  });

  // --------- JSX

  return (
    <div className="flex h-auto w-full flex-grow flex-col gap-3">
      {/*******************************************/}
      {/* Posts */}
      {/*******************************************/}

      <BookingDetailsItem title="Posts">
        <>
          {posts && posts.length
            ? posts
                .filter(
                  (post) =>
                    post.source !== 1 &&
                    post.text !== null &&
                    (post.text as string).trim().length !== 0,
                )
                .map((post, idx) => (
                  <p
                    key={idx}
                    className="mb-4"
                  >
                    <span className="mr-3 text-gray-500">
                      {getFormattedTimeAndDate(post.createdOn)}:
                    </span>
                    <span>{convertBrToLineBreaks(post.text as string)}</span>
                  </p>
                ))
            : "No posts."}
        </>
      </BookingDetailsItem>

      <Divider className="my-4" />

      {/*******************************************/}
      {/* Notes */}
      {/*******************************************/}

      <BookingDetailsItem title="Notes">
        <>
          {notes && notes.length
            ? notes
                .filter(
                  (note) => note.noteText !== null && (note.noteText as string).trim().length !== 0,
                )
                .map((note, idx) => (
                  <p
                    key={idx}
                    className="mb-4"
                  >
                    <span className="mr-3 text-gray-500">
                      {getFormattedTimeAndDate(note.createdOn)}:
                    </span>
                    <span>{convertBrToLineBreaks(note.noteText as string)}</span>
                  </p>
                ))
            : "No notes."}
        </>
      </BookingDetailsItem>
    </div>
  );
};

export default BookingPostsNotes;
